// XLInput - Sprint 44 XL.
// Input con label, estados (normal/focus/error), hint y soporte dark mode.
// Sin deps externas. Reenvia ref con forwardRef para integrar con forms.
import { forwardRef, useEffect, useId } from 'react';
import { ensureKeyframes, injectCSS } from '../../design/motion';

const CSS_ID = 'xl-input-css';
const CSS = `
.xl-field { display: flex; flex-direction: column; gap: 4px; font-family: var(--xl-font-sans) }
.xl-field__label {
  font-size: 12px; font-weight: 500;
  color: var(--xl-color-text-secondary);
  letter-spacing: 0.01em;
}
.xl-input {
  width: 100%;
  font: 400 14px/1.4 var(--xl-font-sans);
  color: var(--xl-color-text);
  background: var(--xl-color-surface);
  border: 1px solid var(--xl-color-border);
  border-radius: 8px;
  padding: 10px 12px;
  box-sizing: border-box;
  transition: border-color 120ms cubic-bezier(0,0,0.2,1),
              box-shadow   120ms cubic-bezier(0,0,0.2,1),
              background   120ms cubic-bezier(0,0,0.2,1);
}
.xl-input::placeholder { color: var(--xl-color-text-muted) }
.xl-input:hover { border-color: rgba(46, 55, 69, 0.25) }
.xl-input:focus {
  outline: none;
  border-color: var(--xl-color-focus);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.25);
}
[data-xl-theme="dark"] .xl-input:focus {
  box-shadow: 0 0 0 3px rgba(96,165,250,0.28);
}
.xl-input--error { border-color: var(--xl-color-danger) }
.xl-input--error:focus {
  border-color: var(--xl-color-danger);
  box-shadow: 0 0 0 3px rgba(220,38,38,0.22);
}
.xl-input:disabled {
  background: var(--xl-color-surface-alt);
  color: var(--xl-color-text-muted);
  cursor: not-allowed;
}
.xl-field__hint  { font-size: 11px; color: var(--xl-color-text-muted) }
.xl-field__error { font-size: 11px; color: var(--xl-color-danger) }
`;

const XLInput = forwardRef(function XLInput({
  label, hint, error, id, className = '', ...rest
}, ref) {
  useEffect(() => {
    ensureKeyframes();
    injectCSS(CSS_ID, CSS);
  }, []);

  const autoId = useId();
  const fieldId = id || ('xl-in-' + autoId);

  return (
    <label className={'xl-field ' + className} htmlFor={fieldId}>
      {label && <span className="xl-field__label">{label}</span>}
      <input
        {...rest}
        id={fieldId}
        ref={ref}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={
          error ? fieldId + '-err' : hint ? fieldId + '-hint' : undefined
        }
        className={'xl-input ' + (error ? 'xl-input--error' : '')}
      />
      {!error && hint && (
        <span id={fieldId + '-hint'} className="xl-field__hint">{hint}</span>
      )}
      {error && (
        <span id={fieldId + '-err'} className="xl-field__error" role="alert">
          {error}
        </span>
      )}
    </label>
  );
});

export default XLInput;
